import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { getMonthValues, TranslationPipe } from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { ClientSnapshot } from './model/client-snapshot.model';
import { NewsletterSubscriberSnapshot } from './model/newsletter-subscriber-snapshot.model';
import { GetMonthlyNewsletterSubscribersRequest } from './use-cases/get-monthly-newsletter-subscribers/get-monthly-newsletter-subscribers.request';
import { GetMonthlyNewsletterSubscribersUseCase } from './use-cases/get-monthly-newsletter-subscribers/get-monthly-newsletter-subscribers.usecase';
import { GetMonthlyRegisteredClientsRequest } from './use-cases/get-monthly-registered-clients/get-monthly-registered-clients.request';
import { GetMonthlyRegisteredClientsUseCase } from './use-cases/get-monthly-registered-clients/get-monthly-registered-clients.usecase';
import { GetNewsletterSubscribersCountUseCase } from './use-cases/get-newsletter-subscribers-count/get-newsletter-subscribers-count.usecase';
import { GetRegisteredClientsCountUseCase } from './use-cases/get-registered-clients-count/get-registered-clients-count.usecase';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-clients',
    templateUrl: './clients.component.html',
    styleUrls: ['./clients.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, TranslationPipe],
})
export class ClientsComponent implements OnInit {
    private _router = inject(Router);
    private _cdr = inject(ChangeDetectorRef);
    private _getRegisteredClientsCountUseCase = inject(GetRegisteredClientsCountUseCase);
    private _getNewsletterSubscribersCountUseCase = inject(GetNewsletterSubscribersCountUseCase);
    private _getMonthlyRegisteredClientsUseCase = inject(GetMonthlyRegisteredClientsUseCase);
    private _getMonthlyNewsletterSubscribersUseCase = inject(GetMonthlyNewsletterSubscribersUseCase);

    readonly registeredClientsCount = signal(0);
    readonly newsletterSubscribersCount = signal(0);
    readonly selectedYear = signal(new Date().getFullYear());
    private readonly _monthlyRegisteredClients = signal<ClientSnapshot[]>([]);
    private readonly _monthlyNewsletterSubscribers = signal<NewsletterSubscriberSnapshot[]>([]);

    readonly monthKeys = getMonthValues();

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
            return;
        }

        this._loadCounts();
        this._loadMonthlyStats();
    }

    onPreviousYear(): void {
        this.selectedYear.update((year) => year - 1);
        this._loadMonthlyStats();
    }

    onNextYear(): void {
        this.selectedYear.update((year) => year + 1);
        this._loadMonthlyStats();
    }

    getRegisteredForMonth(month: number): number {
        return this._monthlyRegisteredClients().find((snapshot) => snapshot.month === month)?.quantity ?? 0;
    }

    getSubscribersForMonth(month: number): number {
        return this._monthlyNewsletterSubscribers().find((snapshot) => snapshot.month === month)?.quantity ?? 0;
    }

    private _loadCounts(): void {
        const registeredCount$ = this._getRegisteredClientsCountUseCase.execute() as Observable<any>;
        registeredCount$.subscribe((response) => {
            this.registeredClientsCount.set(response.count);
            this._cdr.markForCheck();
        });

        const subscribersCount$ = this._getNewsletterSubscribersCountUseCase.execute() as Observable<any>;
        subscribersCount$.subscribe((response) => {
            this.newsletterSubscribersCount.set(response.count);
            this._cdr.markForCheck();
        });
    }

    private _loadMonthlyStats(): void {
        const year = this.selectedYear();

        const registeredRequest: GetMonthlyRegisteredClientsRequest = { year };
        const registered$ = this._getMonthlyRegisteredClientsUseCase.execute(registeredRequest) as Observable<any>;
        registered$.subscribe((response) => {
            this._monthlyRegisteredClients.set(response?.clientSnapshotList ?? []);
            this._cdr.markForCheck();
        });

        const subscribersRequest: GetMonthlyNewsletterSubscribersRequest = { year };
        const subscribers$ = this._getMonthlyNewsletterSubscribersUseCase.execute(subscribersRequest) as Observable<any>;
        subscribers$.subscribe((response) => {
            this._monthlyNewsletterSubscribers.set(response?.subscribedForNewsletterSnapshotList ?? []);
            this._cdr.markForCheck();
        });
    }
}
